import React, { useRef, useState } from "react";
import { Button, Flex, Input, message, Modal, Select, Typography } from "antd";
import { CloseCircleOutlined, DeleteOutlined, PaperClipOutlined, PictureOutlined } from "@ant-design/icons";
import {
    CommunicationBodyEditor,
    CommunicationBodyEditorHandle,
} from "./CommunicationBodyEditor";
import { useLocation, useNavigate } from "react-router-dom";
import { PageTitle } from "../../components/Common/PageTitle/PageTitle";
import { Paths } from "../../models/Paths";
import { CATEGORIES, Communication } from "../../models/Communications";
import { CommunicationHttpService } from "../../services/Http/CommunicationHttpService";
import "./css/CommunicationsFormPage.css";

const { Text } = Typography;

export function CommunicationsFormPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const item: Communication | undefined = location.state?.item;
    const isEditing = item != null;

    const [formCategory, setFormCategory] = useState<string | undefined>(item?.category);
    const [formSubject, setFormSubject] = useState(item?.title ?? "");
    const [formBody, setFormBody] = useState("");
    const [sending, setSending] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    // ── Multi-attachment state ─────────────────────────────────────────────────
    /** Attachments already persisted in the DB (shown when editing). */
    const [existingAttachments, setExistingAttachments] = useState<{ id: number; fileName: string }[]>([]);
    /** New files picked by the user — uploaded to the backend on INVIA. */
    const [newFiles, setNewFiles] = useState<File[]>([]);

    const editorRef = useRef<CommunicationBodyEditorHandle>(null);

    // Load body text + existing attachments when editing
    React.useEffect(() => {
        if (isEditing && item) {
            getCommunicationById(item.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function getCommunicationById(id: number) {
        CommunicationHttpService.getById(id)
            .then((res) => {
                setFormBody(res.data.body ?? "");
                setExistingAttachments(res.data.attachments ?? []);
            })
            .catch(() => message.error("Impossibile caricare il messaggio"));
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    function openFileDialog(accept: string, multiple: boolean, onFiles: (files: File[]) => void) {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = accept;
        input.multiple = multiple;
        input.onchange = (e: Event) => {
            const fileList = (e.target as HTMLInputElement).files;
            if (fileList && fileList.length > 0) {
                onFiles(Array.from(fileList));
            }
        };
        input.click();
    }

    // ── Image embed ───────────────────────────────────────────────────────────
    function handleInsertImage() {
        openFileDialog("image/*", false, async ([file]) => {
            setImageUploading(true);
            try {
                const res = await CommunicationHttpService.uploadImage(file);
                editorRef.current?.insertAtCursor(`{{IMAGE:${res.data.id}}}`);
            } catch {
                message.error("Errore durante il caricamento dell'immagine");
            } finally {
                setImageUploading(false);
            }
        });
    }

    // ── Attachments ───────────────────────────────────────────────────────────
    function handleAddFiles() {
        openFileDialog("*/*", true, (files) => {
            setNewFiles((prev) => [...prev, ...files]);
        });
    }

    function handleRemoveNewFile(index: number) {
        setNewFiles((prev) => prev.filter((_, i) => i !== index));
    }

    async function handleRemoveExistingAttachment(attachmentId: number) {
        try {
            await CommunicationHttpService.deleteAttachment(item!.id, attachmentId);
            setExistingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
        } catch {
            message.error("Errore durante l'eliminazione dell'allegato");
        }
    }

    // ── Send ──────────────────────────────────────────────────────────────────
    async function handleSend() {
        if (!formSubject.trim()) { message.warning("Inserisci l'oggetto del messaggio"); return; }
        if (!formCategory) { message.warning("Seleziona una categoria"); return; }

        setSending(true);
        try {
            const payload = { title: formSubject, category: formCategory, body: formBody };
            const res = isEditing
                ? await CommunicationHttpService.update(item!.id, payload)
                : await CommunicationHttpService.create(payload);

            for (const file of newFiles) {
                await CommunicationHttpService.uploadAttachment(res.data.id, file);
            }

            message.success(isEditing ? "Comunicazione aggiornata" : "Comunicazione inviata");
            navigate(Paths.COMMUNICATIONS);
        } catch {
            message.error("Errore durante il salvataggio");
        } finally {
            setSending(false);
        }
    }

    // ── Delete ────────────────────────────────────────────────────────────────
    async function handleDelete() {
        setSending(true);
        try {
            await CommunicationHttpService.delete(item!.id);
            message.success("Comunicazione eliminata");
            setDeleteModalOpen(false);
            navigate(Paths.COMMUNICATIONS);
        } catch {
            message.error("Errore durante l'eliminazione");
        } finally {
            setSending(false);
        }
    }

    const hasAttachments = existingAttachments.length > 0 || newFiles.length > 0;

    return (
        <div className="cform-page">
            <PageTitle
                title="News e Comunicazioni"
                onClick={() => navigate(Paths.COMMUNICATIONS)}
            />

            <div className="cform-card">
                {/* ── Header ────────────────────────────────────────── */}
                <Flex justify="space-between" align="center" className="cform-header">
                    <Text strong className="cform-title">
                        {isEditing ? "Modifica messaggio" : "Nuovo messaggio"}
                    </Text>
                    <Select
                        className="cform-category"
                        placeholder="Categoria"
                        value={formCategory}
                        onChange={setFormCategory}
                        options={CATEGORIES.map((c) => ({ value: c, label: c }))}
                    />
                </Flex>

                {/* ── Subject ───────────────────────────────────────── */}
                <Input
                    className="cform-subject"
                    placeholder="Oggetto del messaggio"
                    value={formSubject}
                    onChange={(e) => setFormSubject(e.target.value)}
                />

                {/* ── Body ──────────────────────────────────────────── */}
                <div className="cform-body-wrap">
                    <CommunicationBodyEditor
                        ref={editorRef}
                        body={formBody}
                        onChange={setFormBody}
                    />

                    {/* ── Attachment chips ──────────────────────────── */}
                    {hasAttachments && (
                        <div className="cform-attachments-bar">
                            {existingAttachments.map((att) => (
                                <div key={`existing-${att.id}`} className="cform-attachment-chip">
                                    <PaperClipOutlined className="cform-chip-icon" />
                                    <span className="cform-chip-name">{att.fileName}</span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        className="cform-chip-delete"
                                        onClick={() => handleRemoveExistingAttachment(att.id)}
                                    />
                                </div>
                            ))}
                            {newFiles.map((file, idx) => (
                                <div key={`new-${idx}`} className="cform-attachment-chip cform-attachment-chip--new">
                                    <PaperClipOutlined className="cform-chip-icon" />
                                    <span className="cform-chip-name">{file.name}</span>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        className="cform-chip-delete"
                                        onClick={() => handleRemoveNewFile(idx)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Footer ────────────────────────────────────────── */}
                <Flex justify="space-between" align="center" className="cform-footer">
                    <Flex gap="small">
                        <Button
                            type="primary"
                            onClick={handleAddFiles}
                        >
                            ALLEGA
                        </Button>
                        <Button
                            icon={<PictureOutlined />}
                            onClick={handleInsertImage}
                            loading={imageUploading}
                        >
                            IMMAGINE
                        </Button>
                    </Flex>
                    <Flex gap="middle">
                        <Button onClick={() => navigate(Paths.COMMUNICATIONS)}>ANNULLA</Button>
                        {isEditing && (
                            <Button danger onClick={() => setDeleteModalOpen(true)}>
                                ELIMINA
                            </Button>
                        )}
                        <Button type="primary" onClick={handleSend} loading={sending}>
                            INVIA
                        </Button>
                    </Flex>
                </Flex>
            </div>

            {/* ── Delete confirmation modal ─────────────────────────── */}
            <Modal
                open={deleteModalOpen}
                onCancel={() => setDeleteModalOpen(false)}
                footer={null}
                centered
                closable
                closeIcon={<CloseCircleOutlined style={{ fontSize: 22 }} />}
                width={400}
            >
                <div className="cform-delete-body">
                    <Text className="cform-delete-text">
                        Sei sicuro di voler eliminare la comunicazione?
                    </Text>
                    <Flex justify="center" gap="middle">
                        <Button onClick={() => setDeleteModalOpen(false)}>ANNULLA</Button>
                        <Button type="primary" danger onClick={handleDelete} loading={sending}>
                            ELIMINA
                        </Button>
                    </Flex>
                </div>
            </Modal>
        </div>
    );
}
